import { useState, useCallback, useRef } from 'react';
import type {
  Message,
  SendMessageResponse,
  ConversationResponse,
  PaymentIntentResponse,
} from '../types';
import { ApiService, ApiError } from '../services/api';
import { getVisitorId } from '../services/storage';

interface UseChatOptions {
  apiService: ApiService | null;
  tenantSlug: string;
  conversationId: string | null;
  sessionId: string | null;
  onConversationStart?: (conversationId: string, sessionId: string) => void;
}

interface UseChatReturn {
  messages: Message[];
  isLoading: boolean;
  isSending: boolean;
  sendMessage: (content: string, wasVoiceInput?: boolean) => Promise<void>;
  acceptOffer: (paymentType: 'deposit' | 'full') => Promise<PaymentIntentResponse | null>;
  loadHistory: () => Promise<void>;
  clearMessages: () => void;
  error: string | null;
  resetError: () => void;
}

export function useChat({
  apiService,
  tenantSlug,
  conversationId: initialConversationId,
  sessionId: initialSessionId,
  onConversationStart,
}: UseChatOptions): UseChatReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const conversationIdRef = useRef<string | null>(initialConversationId);
  const sessionIdRef = useRef<string | null>(initialSessionId);

  // Update refs when props change (guard against null overwrites)
  if (initialConversationId && initialConversationId !== conversationIdRef.current) {
    conversationIdRef.current = initialConversationId;
  }
  if (initialSessionId && initialSessionId !== sessionIdRef.current) {
    sessionIdRef.current = initialSessionId;
  }

  const loadHistory = useCallback(async (): Promise<void> => {
    if (!apiService || !conversationIdRef.current || !sessionIdRef.current) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response: ConversationResponse = await apiService.getConversation(
        conversationIdRef.current,
        sessionIdRef.current
      );

      setMessages(response.messages);
    } catch (err) {
      const errorMessage = err instanceof ApiError
        ? err.message
        : 'Failed to load conversation history';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [apiService]);

  const startConversation = useCallback(
    async (): Promise<string | null> => {
      if (!apiService) {
        setError('Widget not initialized');
        return null;
      }

      // Get session_id from ref (set by widget init) and visitor_id
      const currentSessionId = sessionIdRef.current;
      const visitorId = getVisitorId();

      if (!currentSessionId) {
        setError('Session not initialized');
        return null;
      }

      try {
        const response: ConversationResponse = await apiService.startConversation(
          currentSessionId,
          visitorId
        );

        conversationIdRef.current = response.conversationId;

        // Notify parent
        onConversationStart?.(response.conversationId, response.sessionId);

        // Set initial messages if any
        if (response.messages.length > 0) {
          setMessages(response.messages);
        }

        return response.conversationId;
      } catch (err) {
        const errorMessage = err instanceof ApiError
          ? err.message
          : 'Failed to start conversation';
        setError(errorMessage);
        return null;
      }
    },
    [apiService, tenantSlug, onConversationStart]
  );

  const sendMessage = useCallback(
    async (content: string, wasVoiceInput: boolean = false): Promise<void> => {
      if (!apiService) {
        setError('Widget not initialized');
        return;
      }

      const trimmedContent = content.trim();
      if (!trimmedContent) {
        return;
      }

      setIsSending(true);
      setError(null);

      try {
        // Start conversation if needed
        if (!conversationIdRef.current) {
          const newConversationId = await startConversation();
          if (!newConversationId) {
            setIsSending(false);
            return;
          }
        }

        // Create optimistic user message
        const optimisticUserMessage: Message = {
          id: `temp-${Date.now()}`,
          conversationId: conversationIdRef.current!,
          role: 'user',
          content: trimmedContent,
          metadata: null,
          wasVoiceInput,
          createdAt: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, optimisticUserMessage]);

        // Send message to API
        const response: SendMessageResponse = await apiService.sendMessage(
          conversationIdRef.current!,
          sessionIdRef.current!,
          trimmedContent,
          wasVoiceInput
        );

        // Replace optimistic message with actual response
        setMessages((prev) => {
          const filtered = prev.filter((m) => m.id !== optimisticUserMessage.id);
          return [...filtered, response.message, response.assistantMessage];
        });
      } catch (err) {
        // Remove optimistic message on error
        setMessages((prev) => prev.filter((m) => !m.id.startsWith('temp-')));

        const errorMessage = err instanceof ApiError
          ? err.message
          : 'Failed to send message';
        setError(errorMessage);
      } finally {
        setIsSending(false);
      }
    },
    [apiService, startConversation]
  );

  const acceptOffer = useCallback(
    async (paymentType: 'deposit' | 'full'): Promise<PaymentIntentResponse | null> => {
      if (!apiService || !conversationIdRef.current || !sessionIdRef.current) {
        setError('No active conversation');
        return null;
      }

      setIsSending(true);
      setError(null);

      try {
        const response: PaymentIntentResponse = await apiService.acceptOffer(
          conversationIdRef.current,
          sessionIdRef.current,
          paymentType
        );

        return response;
      } catch (err) {
        const errorMessage = err instanceof ApiError
          ? err.message
          : 'Failed to accept offer';
        setError(errorMessage);
        return null;
      } finally {
        setIsSending(false);
      }
    },
    [apiService]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    conversationIdRef.current = null;
    sessionIdRef.current = null;
  }, []);

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    isSending,
    sendMessage,
    acceptOffer,
    loadHistory,
    clearMessages,
    error,
    resetError,
  };
}

export type { UseChatReturn };
